import { DefaultApi } from '../autogenerated/spdApiClient/apis/DefaultApi';
import {
  CreateUpdateHealthcareFacilityDto,
  CreateUpdateHealthcareFacilityDtoFromJSON,
  GetHealthcareFacilityDto,
  GetLastEditorDto,
  SetFlagHealthcareFacilityDto,
} from '../autogenerated/spdApiClient';
import { healthcareFacilityForGrid } from '../../../spd/components/HealthcareFacility/HealthcareFacilitySearch';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { validateAgainst } from '../validation/validateAgainst';
import { HealthcareFacilitySchema } from '../validation/schemas';

export interface HealthcareFacilityContextInterface {
  get: (id: string) => Promise<GetHealthcareFacilityDto>;
  getSpecific: (
    id: string,
    dataId: string,
  ) => Promise<GetHealthcareFacilityDto>;
  getDefault: () => Promise<string>;
  getAll: (canSeeAll?: boolean) => Promise<GetHealthcareFacilityDto[]>;
  getAllCount: (filterText?: string) => Promise<number>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    onSetAsMain?: (facilityId: string, facilityName: string) => void,
    onSetAsOnline?: (facilityId: string, facilityName: string) => void,
  ) => Promise<GridRecord[]>;
  getAllHistoricalCount: (filterText?: string) => Promise<number>;
  getAllHistoricalByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
  ) => Promise<GridRecord[]>;
  update: (id: string, dto: CreateUpdateHealthcareFacilityDto) => Promise<void>;
  create: (
    dto: CreateUpdateHealthcareFacilityDto,
  ) => Promise<GetHealthcareFacilityDto>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  setFlag: (
    id: string,
    dto: SetFlagHealthcareFacilityDto,
  ) => Promise<GetHealthcareFacilityDto>;
}

export const HealthcareFacilityContext = (api: DefaultApi) => {
  return {
    getSpecific: async (id: string, dataId: string) => {
      return await api.healthcareFacilityControllerGetWithDataId(id, dataId);
    },
    get: async (id: string) => {
      return await api.healthcareFacilityControllerGet(id);
    },
    getDefault: async () => {
      return await api.healthcareFacilityControllerGetDefault();
    },
    getAll: async (canSeeAll?: boolean) => {
      return await api.healthcareFacilityControllerGetAll(canSeeAll);
    },
    getAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      onSetAsMain?: (facilityId: string, facilityName: string) => void,
      onSetAsOnline?: (facilityId: string, facilityName: string) => void,
    ) => {
      const data = await api.healthcareFacilityControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
      );
      return data.map(healthcareFacility =>
        healthcareFacilityForGrid(
          healthcareFacility,
          onSetAsMain,
          onSetAsOnline,
        ),
      ) as GridRecord[];
    },

    getAllCount: (filterText?: string) =>
      api.healthcareFacilityControllerGetAllCount(filterText || ''),

    getAllHistoricalByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) => {
      const data =
        await api.healthcareFacilityControllerGetAllHistoricalByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
        );
      const expanded = data.flatMap(hf =>
        (hf.healthcareFacilityData || [])
          .sort(
            (a, b) =>
              new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime(),
          )
          .map(childData => {
            const gridRecord = healthcareFacilityForGrid({
              ...hf,
              healthcareFacilityData: [childData],
            });
            // Use composite key for historical data to differentiate between rows of same facility
            return {
              ...gridRecord,
              key: `${hf.id}|${childData.id}`,
            };
          }),
      );
      return expanded as GridRecord[];
    },

    getAllHistoricalCount: (filterText?: string) =>
      api.healthcareFacilityControllerGetAllHistoricalCount(filterText || ''),

    update: (id: string, dto: CreateUpdateHealthcareFacilityDto) =>
      api.healthcareFacilityControllerUpdate(id, dto),

    create: (dto: CreateUpdateHealthcareFacilityDto) =>
      api.healthcareFacilityControllerCreate(dto),

    getLastEditor: (id: string) =>
      api.healthcareFacilityControllerGetLastEditor(id),

    setFlag: (id: string, dto: SetFlagHealthcareFacilityDto) =>
      api.healthcareFacilityControllerSetFlag(id, dto),
  };
};

export const validate = (
  healthcareFacility: CreateUpdateHealthcareFacilityDto,
) => {
  return validateAgainst(HealthcareFacilitySchema, healthcareFacility);
};

export const healthcareFacilityData = (
  healthcareFacility?: GetHealthcareFacilityDto,
): CreateUpdateHealthcareFacilityDto => {
  return CreateUpdateHealthcareFacilityDtoFromJSON({
    ...healthcareFacility,
    ...healthcareFacility?.healthcareFacilityData?.[0],
    dutyId: healthcareFacility?.duty?.id,
  });
};
